<template>
  <div class="mx-auto mt-16 font-montserrat max-w-lg">
     <img
    src="@/app/ui/layout/sidebar/lllustrations/logo-general-center-text.svg"
    class="w-auto mx-auto h-16"
    :alt="$t('resetPasswordPage.logoAlt')"
  />
    <p class=" text-42px font-montserrat text-center font-semibold text-black-lp-100 mt-6 mb-8">
      {{ $t("resetPasswordPage.title") }}
    </p>
    <p class="font-montserrat text-sm font-semibold mt-4 text-gray-lp-300">
       {{ $t("resetPasswordPage.newPasswordLabel") }}
    </p>
    <div class="inline-block relative w-full">
      <input
        data-testid="auth-reset-password-input-new-password"
        :type="newPasswordType"
        v-model="newPassword"
        @input="checkPassword"
        :placeholder="$t('resetPasswordPage.newPasswordPlaceholder')"
        :class="{
          'border-red-lp-2000 focus:border-red-lp-2000': errorModal
        }"
        class="w-full mt-2 h-10 pl-4 border rounded-md shadow-md font-montserrat focus:outline-none"
      />
      <button
        @click="showNewPassword"
        class="cursor-pointer absolute inset-y-0 right-0 flex items-center px-6 pt-2 text-gray-500 focus:outline-none"
      >
        <Icons
          v-if="iconType === 'eye'"
          name="eye"
          class="h-6 text-gray-lp-600"
        />
        <Icons v-else name="invisible" class="h-6 text-gray-lp-600" />
      </button>
    </div>
       <div
          v-if="errorModal"
          class="text-red-lp-2000 text-12px mt-1 xxl:text-14px flex pl-1 gap-x-2"
        >
          <img
            src="@/app/ui/assets/svg/exclamation-triangle-red.svg"
            class="w-4.5 max-w-none"
            :alt="$t('resetPasswordPage.errorIconAlt')"
          />
          <span>{{ $t("resetPasswordPage.passwordReuseError") }}</span>
        </div>
    <p class="font-montserrat text-sm font-semibold mt-4 text-gray-lp-300">
      {{ $t("resetPasswordPage.confirmNewPasswordLabel") }}
    </p>
    <div class="inline-block relative w-full">
      <input
        data-testid="auth-reset-password-input-confirm-password"
        :type="confirmPasswordType"
        v-model="confirmPassword"
        @input="checkPassword"
        :placeholder="$t('resetPasswordPage.confirmNewPasswordPlaceholder')"
        class="w-full mt-2 h-10 pl-4 border rounded-md shadow-md font-montserrat focus:outline-none"
        :class="{
          'border-red-lp-2000': !validPassword && confirmPassword || errorModal
        }"
      />
      <button
        @click="showConfirmPassword"
        class="cursor-pointer absolute inset-y-0 right-0 flex items-center px-6 pt-2 text-gray-500 focus:outline-none"
      >
        <Icons
          v-if="iconTypeConfirm === 'eye'"
          name="eye"
          class="h-6 text-gray-lp-600"
        />
        <Icons v-else name="invisible" class="h-6 text-gray-lp-600" />
      </button>
      
    </div>
     <div
          v-if="errorModal"
          class="text-red-lp-2000 text-12px mt-1 xxl:text-14px flex pl-1 gap-x-2"
        >
          <img
            src="@/app/ui/assets/svg/exclamation-triangle-red.svg"
            class="w-4.5 max-w-none"
            :alt="$t('resetPasswordPage.errorIconAlt')"
          />
          <span>{{ $t("resetPasswordPage.passwordReuseError") }}</span>
        </div>
    <div class="my-4 mx-4 flex flex-col gap-y-2">
      <div class="flex flex-row items-center space-x-3">
        <Icons
          v-if="containsEightCharacters"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-green-lp-100"
        />
        <Icons
          v-if="!containsEightCharacters && newPassword"
          name="x-circle"
          class="w-5 h-5 shrink-0 text-red-lp-400"
        />
        <Icons
          v-if="!newPassword"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-gray-lp-500"
        />
        <p class="text-sm opacity-50 text-gray-lp-300">{{ $t("resetPasswordPage.rules.minEightChars") }}</p>
      </div>
      <div class="flex flex-row items-center space-x-3">
        <Icons
          v-if="containsNumber"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-green-lp-100"
        />
        <Icons
          v-if="!containsNumber && newPassword"
          name="x-circle"
          class="w-5 h-5 shrink-0 text-red-lp-400"
        />
        <Icons
          v-if="!newPassword"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-gray-lp-500"
        />
        <p class="text-sm opacity-50 text-gray-lp-300">{{ $t("resetPasswordPage.rules.containsNumber") }}</p>
      </div>
      <div class="flex flex-row items-center space-x-3">
        <Icons
          v-if="containsUppercase"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-green-lp-100"
        />
        <Icons
          v-if="!containsUppercase && newPassword"
          name="x-circle"
          class="w-5 h-5 shrink-0 text-red-lp-400"
        />
        <Icons
          v-if="!newPassword"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-gray-lp-500"
        />
        <p class="text-sm opacity-50 text-gray-lp-300">
            {{ $t("resetPasswordPage.rules.containsUppercase") }}
        </p>
      </div>
      <div class="flex flex-row items-center space-x-3">
        <Icons
          v-if="validPassword"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-green-lp-100"
        />
        <Icons
          v-if="!validPassword && confirmPassword"
          name="x-circle"
          class="w-5 h-5 shrink-0 text-red-lp-400"
        />
        <Icons
          v-if="!confirmPassword"
          name="check-circle"
          class="w-5 h-5 shrink-0 text-gray-lp-500"
        />
        <p class="text-sm opacity-50 text-gray-lp-300">{{ $t("resetPasswordPage.rules.confirmPassword") }}</p>
      </div>
    </div>
    <button
      data-testid="auth-reset-password-button-submit"
      type="submit"
      class="text-white py-2 my-4 font-semibold rounded-md focus:outline-none bg-gray-lp-600 w-full"
      disabled
      v-if="
        !validPassword ||
          !containsEightCharacters ||
          !containsNumber ||
          !containsUppercase
      "
    >
      <span>{{ $t("resetPasswordPage.submitButton") }}</span>
    </button>

    <button
      data-testid="auth-reset-password-button-submit"
      type="submit"
      class="text-white py-2 my-4 font-semibold rounded-md focus:outline-none bg-red-lp-100 w-full"
      v-else
      @click="submit()"
    >
      {{ $t("resetPasswordPage.submitButton") }}
    </button>
    <div class="force-change-password-help">
      {{ $t("verificationAccount.helpWhatsApp") }}
      <a
        class="text-red-lp-100 underline"
        :href="`https://wa.me/${cleanPhoneNumber(phoneNumberHD)}`"
        target="_blank"
        >{{ phoneNumberHD }}</a
      >
      {{ $t("verificationAccount.orEmailTo") }}
      <a
        class="text-red-lp-100 underline"
        href="mailto:system@thelionparcel.com"
        >system@thelionparcel.com</a
      >
    </div>
  </div>
  <Footer />
  <Modal
    v-if="isOpen"
    :onSubmit="onClose"
    :title="$t('resetPasswordPage.successModal.title')"
    :message="$t('resetPasswordPage.successModal.message')"
    image="password_saved"
    :textSuccess="$t('resetPasswordPage.ok')"
  />
  <!--    loading   -->
  <loading v-model="loadingModal" />
  <!--    modal error   -->
  <Modal
    v-if="errorModal && !/^New password must not be the same as the last (\d+) passwords\.$/.test(errorEntities?.message)"
    :textCancel="errorTextCancel"
    :onClose="closeErrorModal"
    :onSubmit="errorOnSubmit"
    :textSuccess="errorTextSuccess"
    :title="errorEntities.title"
    :message="errorEntities.message"
    :image="errorEntities.image"
  />
</template>

<script>
import { Vue, Options } from "vue-class-component";
import { AuthController } from "@/app/ui/controllers/AuthController";
import Footer from "@/app/ui/components/footer/index.vue";
import router from "../../router";
import Loading from "@/app/ui/components/loading/index.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { cleanPhoneNumber } from "@/app/infrastructures/misc/common-library/Whatsapp";
import { PHONE_NUMBER_HD } from "@/app/infrastructures/misc/Constants";

@Options({
  name: "ResetPasswordPage",
  components: {
    Footer,
    Loading
  }
})
export default class ResetPassword extends Vue {
  newPassword = "";
  confirmPassword = "";
  passwordLength = 0;
  containsEightCharacters = false;
  containsNumber = false;
  containsUppercase = false;
  validPassword = false;

  newPasswordType = "password";
  confirmPasswordType = "password";
  iconType = "eye-slash";
  iconTypeConfirm = "eye-slash";
  cleanPhoneNumber = cleanPhoneNumber;

  get phoneNumberHD() {
    return PHONE_NUMBER_HD
  }

  get isOpen() {
    return AuthController.openModalSuccess;
  }

  submit() {
    AuthController.changePassword({
      email: this.$route.query.email,
      password: this.newPassword,
      token: this.$route.query.token
    });
  }

  onClose() {
    AuthController.setModalSuccess(false);
    router.push("/");
  }

  showNewPassword() {
    if (this.newPasswordType === "password" && this.iconType === "eye-slash") {
      this.newPasswordType = "text";
      this.iconType = "eye";
    } else {
      this.newPasswordType = "password";
      this.iconType = "eye-slash";
    }
  }

  showConfirmPassword() {
    if (
      this.confirmPasswordType === "password" &&
      this.iconTypeConfirm === "eye-slash"
    ) {
      this.confirmPasswordType = "text";
      this.iconTypeConfirm = "eye";
    } else {
      this.confirmPasswordType = "password";
      this.iconTypeConfirm = "eye-slash";
    }
  }

  checkPassword() {
    MainAppController.closeErrorMessage();
    this.passwordLength = this.newPassword.length;

    this.containsEightCharacters = this.passwordLength >= 8;

    this.containsNumber = /\d/.test(this.newPassword);
    this.containsUppercase = /[A-Z]/.test(this.newPassword);
    this.validPassword =
      this.containsEightCharacters === true &&
      this.containsNumber === true &&
      this.containsUppercase === true &&
      this.newPassword === this.confirmPassword;
  }

  // loading
  get loadingModal() {
    return MainAppController.loadingModal;
  }

  // error modal
  get errorModal() {
    return MainAppController.errorMessageModal;
  }
  get errorEntities() {
    return MainAppController.errorMessageEntities;
  }
  get errorTextCancel() {
    return this.errorEntities.onTryAgain ? this.$t("resetPasswordPage.back") : "";
  }
  get errorTextSuccess() {
    return this.errorEntities.onTryAgain ? this.$t("resetPasswordPage.tryAgain") : this.$t("resetPasswordPage.ok");
  }
  get errorOnSubmit() {
    return this.errorEntities.onTryAgain
      ? this.errorEntities.onTryAgain
      : this.closeErrorModal;
  }
  closeErrorModal() {
    this.errorEntities.onClose && this.errorEntities.onClose();
    MainAppController.closeErrorMessage();
  }
}
</script>
<style scoped>
.force-change-password-help {
  @apply my-6 text-sm mx-auto;

  width: 420px;
  text-align: center;
}
</style>
